import { useState, useEffect, ReactNode } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { CurrentUserContext, type CurrentUserState, type UserType } from './useCurrentUser.context';
import { getSignerSessionClaims } from '../auth/signer-session';

// Type-only re-exports are erased at compile time and do not break Fast
// Refresh, so the rule allows them alongside the Provider component below.
// Runtime consumers (the `useCurrentUser` hook itself) must import from
// `./useCurrentUser.context` directly.
export type { CurrentUserState, UserType } from './useCurrentUser.context';

/**
 * Reads the current auth session and derives the user state from it. Lives at
 * module scope so the provider's effect can own the whole async path itself
 * (#3423) rather than calling a component-scope function that writes state.
 *
 * The signer branch returns without awaiting anything, but the caller still
 * awaits this function, so no state is written synchronously from the effect
 * body on that path either.
 */
async function deriveCurrentUserState(): Promise<CurrentUserState> {
    // Email-only signer (#2955): a self-issued signer JWT in sessionStorage
    // is the source of truth and there is NO Cognito session to read. Derive
    // state from its claims (always userType 'signer') and skip Amplify.
    const signerClaims = getSignerSessionClaims();
    if (signerClaims) {
        return {
            displayName: signerClaims.email || null,
            email: signerClaims.email || null,
            userType: 'signer',
            isAdmin: false,
            loading: false,
        };
    }

    try {
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload;

        const givenName = payload?.given_name as string | undefined;
        const familyName = payload?.family_name as string | undefined;
        const email = payload?.email as string | undefined;
        const parts = [givenName, familyName].filter(Boolean);
        const name = parts.join(' ') || email || '';

        const rawUserType = payload?.['custom:userType'] as string | undefined;
        const userType: UserType | null = rawUserType === 'admin' || rawUserType === 'signer' ? rawUserType : null;

        return {
            displayName: name || null,
            email: email || null,
            userType,
            isAdmin: userType === 'admin',
            loading: false,
        };
    } catch {
        return {
            displayName: null,
            email: null,
            userType: null,
            isAdmin: false,
            loading: false,
        };
    }
}

/**
 * Provides the current authenticated user's derived attributes (display name,
 * email, user type, admin flag) from the Cognito id token.
 *
 * The attributes are read once on mount and re-read whenever Amplify emits an
 * `auth` Hub event that indicates the token changed (`tokenRefresh`) or a new
 * sign-in occurred (`signedIn`). This keeps the nav and any other consumers in
 * sync after a role change in Cognito without requiring a full page reload —
 * the next token refresh carries the updated `custom:userType` claim and the
 * provider re-derives state from it.
 */
export function CurrentUserProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<CurrentUserState>({
        displayName: null,
        email: null,
        userType: null,
        isAdmin: false,
        loading: true,
    });

    // One effect owns both the initial derivation and the Hub subscription so
    // the async work stays inside the effect body (#3423). It re-derives on an
    // Amplify auth Hub event that carries new claims: a `tokenRefresh` (e.g. an
    // updated `custom:userType` after a role change in Cognito) or a `signedIn`
    // marking a fresh session. Other auth events (signOut, failures, etc.) are
    // ignored. `Hub.listen` returns its own unsubscribe function.
    useEffect(() => {
        let cancelled = false;

        const apply = async (): Promise<void> => {
            const next = await deriveCurrentUserState();
            if (cancelled) return;
            setState(next);
        };

        void apply();

        const unsubscribe = Hub.listen('auth', ({ payload }) => {
            if (payload.event === 'tokenRefresh' || payload.event === 'signedIn') {
                void apply();
            }
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, []);

    return <CurrentUserContext.Provider value={state}>{children}</CurrentUserContext.Provider>;
}
